import React from 'react';
import { pinyin } from 'pinyin-pro';
import { MapPin, X } from 'lucide-react';
import { AIRPORTS, loadAirports, airportByCode } from './flightData';

// 机场目录有 490+ 条，下拉框已经不可用，改为可输入搜索。
// 支持四种输入：中文城市名、拼音（全拼/首字母）、英文城市名、IATA 三字码。

const pinyinCache = new Map();

function pinyinKeys(cityZh) {
  let hit = pinyinCache.get(cityZh);
  if (!hit) {
    hit = {
      full: pinyin(cityZh, { toneType: 'none', type: 'array' }).join(''),
      initials: pinyin(cityZh, { pattern: 'first', toneType: 'none', type: 'array' }).join(''),
    };
    pinyinCache.set(cityZh, hit);
  }
  return hit;
}

function match(airport, q) {
  if (airport.code.toLowerCase().startsWith(q)) return 0;          // 三字码优先
  if (airport.city_zh.includes(q)) return 1;
  const { full, initials } = pinyinKeys(airport.city_zh);
  if (initials.startsWith(q)) return 2;
  if (full.startsWith(q)) return 3;
  if ((airport.city_en || '').toLowerCase().includes(q)) return 4;
  if ((airport.airport_zh || '').includes(q)) return 5;
  return -1;
}

const AirportPicker = ({ value, onChange, exclude, label, placeholder = '城市 / 拼音 / 三字码' }) => {
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const [, forceRender] = React.useReducer((x) => x + 1, 0);
  const boxRef = React.useRef(null);

  // 目录异步加载完成后需要重渲染，才能显示完整列表
  React.useEffect(() => { loadAirports().then(forceRender); }, []);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const results = React.useMemo(() => {
    const pool = AIRPORTS.filter((a) => a.code !== exclude);
    const q = query.trim().toLowerCase();
    if (!q) return pool.slice(0, 30);
    return pool
      .map((a) => ({ a, rank: match(a, q) }))
      .filter((r) => r.rank >= 0)
      .sort((x, y) => x.rank - y.rank || x.a.city_zh.localeCompare(y.a.city_zh, 'zh'))
      .slice(0, 30)
      .map((r) => r.a);
  }, [query, exclude, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = value ? airportByCode(value) : null;

  const pick = (a) => {
    onChange(a.code);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && results[active]) { e.preventDefault(); pick(results[active]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef}>
      {label && <label className="block text-xs text-gray-500 mb-1">{label}</label>}

      {selected && !open ? (
        <button
          type="button"
          onClick={() => { setOpen(true); setActive(0); }}
          className="w-full h-12 px-3 border border-gray-200 rounded-xl bg-white text-left flex items-center justify-between hover:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400"
        >
          <span className="truncate">
            <span className="font-medium text-gray-900">{selected.city_zh}</span>
            <span className="text-gray-400 text-sm ml-2">{selected.code}</span>
          </span>
          <MapPin className="w-4 h-4 text-gray-300 flex-shrink-0" />
        </button>
      ) : (
        <div className="relative">
          <input
            autoFocus={open}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); setOpen(true); }}
            onFocus={() => { setOpen(true); setActive(0); }}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className="w-full h-12 px-3 pr-9 border border-gray-200 rounded-xl bg-white text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
          />
          {selected && (
            <button
              type="button"
              onClick={() => { setQuery(''); setOpen(false); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-300 hover:text-gray-500"
              aria-label="取消"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl">
          {results.length === 0 ? (
            <p className="px-3 py-4 text-sm text-gray-400 text-center">没有匹配的机场</p>
          ) : (
            results.map((a, i) => (
              <button
                key={a.code}
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(a)}
                className={`w-full px-3 py-2.5 text-left flex items-center justify-between gap-2 ${
                  i === active ? 'bg-sky-50' : 'hover:bg-gray-50'
                }`}
              >
                <span className="min-w-0">
                  <span className="font-medium text-gray-900">{a.city_zh}</span>
                  <span className="text-gray-400 text-xs ml-2 truncate">{a.airport_zh}</span>
                </span>
                <span className="text-xs font-mono text-gray-400 flex-shrink-0">{a.code}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AirportPicker;
